'use client';

import { useEffect, type ReactNode } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '@/lib/store';
import { fetchActiveSocialLinks } from '@/lib/store/slices/socialLinkSlice';
import { fetchActiveSettings } from '@/lib/store/slices/settingsSlice';
import { DynamicIcon } from '@/components/ui/DynamicIcon';

interface FooterProps {
  locale: string;
  /** Server-rendered social icon nodes keyed by icon name — keeps react-icons off the client. */
  socialIconNodes?: Record<string, ReactNode>;
}

const t = {
  en: {
    tagline: 'Full Stack Developer',
    bio:     'I build scalable web systems, intelligent chatbots, and business automation tools that turn ideas into production-ready products.',
    nav: {
      label:   'Navigation',
      links: [
        { label: 'About',    href: '/about'   },
        { label: 'Projects', href: '/projects'},
        { label: 'Services', href: '/services'},
        { label: 'Blog',     href: '/blog'    },
        { label: 'FAQ',      href: '/faq'     },
        { label: 'Contact',  href: '/contact' },
      ],
    },
    stack: {
      label: 'Tech Stack',
      items: ['Next.js', 'Node.js', 'MongoDB', 'TypeScript', 'Odoo', 'Python'],
    },
    contact: {
      label:     'Contact',
      available: 'Open to work',
      hire:      'Hire Me',
      phone:     'Phone',
      whatsapp:  'WhatsApp',
    },
    rights:  '© 2025 Mohamed Saber. All rights reserved.',
  },
  ar: {
    tagline: 'مطور ويب متكامل',
    bio:     'أبني أنظمة ويب قابلة للتوسع وروبوتات محادثة ذكية وأدوات أتمتة الأعمال تحوّل الأفكار إلى منتجات جاهزة للإنتاج.',
    nav: {
      label:   'التنقل',
      links: [
        { label: 'عني',           href: '/about'   },
        { label: 'المشاريع',      href: '/projects'},
        { label: 'الخدمات',       href: '/services'},
        { label: 'المدونة',       href: '/blog'    },
        { label: 'الأسئلة الشائعة', href: '/faq'  },
        { label: 'تواصل',         href: '/contact' },
      ],
    },
    stack: {
      label: 'التقنيات',
      items: ['Next.js', 'Node.js', 'MongoDB', 'TypeScript', 'Odoo', 'Python'],
    },
    contact: {
      label:     'التواصل',
      available: 'متاح للعمل',
      hire:      'تواصل معي',
      phone:     'الهاتف',
      whatsapp:  'واتساب',
    },
    rights:  '© 2025 محمد صابر. جميع الحقوق محفوظة.',
  },
} as const;

export default function Footer({ locale, socialIconNodes }: FooterProps) {
  const lang     = locale === 'ar' ? 'ar' : 'en';
  const tx       = t[lang];
  const dispatch = useDispatch<AppDispatch>();
  const { items: socialLinks } = useSelector((s: RootState) => s.socialLinks);
  const settings = useSelector((s: RootState) => s.settings.currentItem);

  useEffect(() => {
    dispatch(fetchActiveSocialLinks());
    dispatch(fetchActiveSettings());
  }, [dispatch]);

  const isAvailable   = settings?.availableForWork ?? true;
  const availableText = (lang === 'ar' ? settings?.availabilityNote?.ar : settings?.availabilityNote?.en)
                        || tx.contact.available;
  const contactEmail  = settings?.contactEmail   || null;
  const contactPhone  = settings?.contactPhone   || null;
  const whatsappNum   = settings?.whatsappNumber || null;
  const siteName      = (lang === 'ar' ? settings?.siteName?.ar : settings?.siteName?.en) || 'Mohamed Saber';
  const siteDesc      = (lang === 'ar' ? settings?.siteDescription?.ar : settings?.siteDescription?.en) || tx.bio;
  const logoUrl       = settings?.logo?.url || '/logo.png';
  const currentYear   = new Date().getFullYear();

  return (
    <footer className="border-t border-(--border) bg-(--surface)">

      {/* ── Main grid ──────────────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Brand ────────────────────────────────────────────────────── */}
          <div className="sm:col-span-2 lg:col-span-1 space-y-5">
            {/* Logo */}
            <Link
              href={`/${locale}`}
              aria-label={`${siteName} — home`}
              className="inline-flex items-center gap-2 group"
            >
              <Image
                src={logoUrl}
                alt={`${siteName} Logo`}
                width={137}
                height={56}
                sizes="80px"
                className="w-auto h-8"
              />
              <span className="text-sm font-semibold text-(--text)">{siteName}</span>
            </Link>

            {/* Tagline */}
            <p className="text-xs font-mono text-primary-500 tracking-widest uppercase">{tx.tagline}</p>

            {/* Bio */}
            <p className="text-sm text-(--muted) leading-relaxed">{siteDesc}</p>

            {/* Social icons from API */}
            {socialLinks.length > 0 && (
              <div className="flex items-center flex-wrap gap-2">
                {socialLinks.map((link) => (
                  <a
                    key={link._id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={locale === 'ar' ? link.platform.ar : link.platform.en}
                    className="
                      w-8 h-8 rounded-lg border border-(--border)
                      flex items-center justify-center
                      text-(--muted) hover:text-primary-500 hover:border-primary-500
                      transition-colors duration-180
                    "
                  >
                    {socialIconNodes?.[link.icon] ? (
                      <span className="inline-flex" style={{ fontSize: 15 }} aria-hidden>
                        {socialIconNodes[link.icon]}
                      </span>
                    ) : (
                      <DynamicIcon
                        iconName={link.icon}
                        size={15}
                        fallback={
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 110-16 8 8 0 010 16z" />
                          </svg>
                        }
                      />
                    )}
                  </a>
                ))}
              </div>
            )}

            {/* Fallback inline socials when API hasn't loaded */}
            {socialLinks.length === 0 && (
              <div className="flex items-center gap-2">
                <a
                  href="https://github.com/MohamedSaber55"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                  className="w-8 h-8 rounded-lg border border-(--border) flex items-center justify-center text-(--muted) hover:text-primary-500 hover:border-primary-500 transition-colors duration-180"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                  </svg>
                </a>
                <a
                  href="https://linkedin.com/in/mohamedsaber5050"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="w-8 h-8 rounded-lg border border-(--border) flex items-center justify-center text-(--muted) hover:text-primary-500 hover:border-primary-500 transition-colors duration-180"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              </div>
            )}
          </div>

          {/* Navigation ──────────────────────────────────────────────── */}
          <div className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-(--text)">{tx.nav.label}</h3>
            <ul className="space-y-2.5">
              {tx.nav.links.map((link) => (
                <li key={link.href}>
                  <Link
                    href={`/${locale}${link.href}`}
                    className="text-sm text-(--muted) hover:text-primary-500 transition-colors duration-180 inline-flex items-center gap-1.5 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-primary-500/0 group-hover:bg-primary-500/60 transition-colors duration-180 shrink-0" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Tech stack ──────────────────────────────────────────────── */}
          <div className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-(--text)">{tx.stack.label}</h3>
            <ul className="space-y-2.5">
              {tx.stack.items.map((tech) => (
                <li key={tech} className="flex items-center gap-2 text-sm text-(--muted)">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500/40 shrink-0" />
                  <span className="font-mono text-xs">{tech}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact ─────────────────────────────────────────────────── */}
          <div className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-(--text)">{tx.contact.label}</h3>
            <div className="space-y-3">

              {/* Email */}
              {contactEmail && (
                <a href={`mailto:${contactEmail}`} className="group flex items-start gap-2.5">
                  <span className="w-7 h-7 rounded-lg bg-(--bg) border border-(--border) flex items-center justify-center shrink-0 mt-0.5 group-hover:border-primary-500/40 transition-colors duration-180">
                    <svg className="w-3.5 h-3.5 text-(--muted) group-hover:text-primary-500 transition-colors duration-180" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </span>
                  <span className="text-sm text-(--muted) group-hover:text-primary-500 transition-colors duration-180 break-all leading-relaxed">
                    {contactEmail}
                  </span>
                </a>
              )}

              {/* Phone */}
              {contactPhone && (
                <a href={`tel:${contactPhone}`} className="group flex items-start gap-2.5">
                  <span className="w-7 h-7 rounded-lg bg-(--bg) border border-(--border) flex items-center justify-center shrink-0 mt-0.5 group-hover:border-primary-500/40 transition-colors duration-180">
                    <svg className="w-3.5 h-3.5 text-(--muted) group-hover:text-primary-500 transition-colors duration-180" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </span>
                  <span className="text-sm text-(--muted) group-hover:text-primary-500 transition-colors duration-180 leading-relaxed" dir="ltr">
                    {contactPhone}
                  </span>
                </a>
              )}

              {/* WhatsApp */}
              {whatsappNum && (
                <a
                  href={`https://wa.me/${whatsappNum.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start gap-2.5"
                >
                  <span className="w-7 h-7 rounded-lg bg-(--bg) border border-(--border) flex items-center justify-center shrink-0 mt-0.5 group-hover:border-green-500/40 transition-colors duration-180">
                    <svg className="w-3.5 h-3.5 text-(--muted) group-hover:text-green-500 transition-colors duration-180" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </span>
                  <span className="text-sm text-(--muted) group-hover:text-green-500 transition-colors duration-180 leading-relaxed" dir="ltr">
                    {whatsappNum}
                  </span>
                </a>
              )}

              {/* Availability badge */}
              <div className="flex items-center gap-2 pt-1">
                <span className={`w-2 h-2 rounded-full shrink-0 ${isAvailable ? 'bg-success-500 animate-pulse' : 'bg-neutral-400'}`} />
                <span className={`text-xs font-medium ${isAvailable ? 'text-success-500' : 'text-(--muted)'}`}>
                  {availableText}
                </span>
              </div>

              {/* CTA */}
              <Link
                href={`/${locale}/contact`}
                className="
                  inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold
                  bg-primary-500 text-neutral-900
                  hover:bg-primary-600 active:bg-primary-700
                  transition-colors duration-180
                "
              >
                {tx.contact.hire}
                <svg className="w-3 h-3 rtl:rotate-180" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── Divider ─────────────────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="h-px bg-linear-to-r from-transparent via-(--border) to-transparent" />
      </div>

      {/* ── Bottom bar ──────────────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-center">
        <p className="text-xs text-(--muted)">
          © {currentYear} {siteName}.{' '}
          {lang === 'ar' ? 'جميع الحقوق محفوظة.' : 'All rights reserved.'}
        </p>
      </div>
    </footer>
  );
}
